import { useNode } from '@craftjs/core';
import { Box } from '@mui/material';
import { useCallback } from 'react';

import { useIsBlockEditorEditMode } from '@/components/blockEditor/BlockEditor.context';
import { Draft } from '@/modules/presentation/clientPresentation/components/slides/EducationalSlide/content/Draft/Draft';

import {
  type EducationListItemType,
  getEducationSlides,
} from '../../Education.constants';
import { useEducationSlideOverride } from '../../EducationSlideOverrides/EducationSlideOverrides.context';
import {
  getSlideIdentifierFromEducationSlideId,
  isCustomSlideIdentifier,
} from '../utils/slideIdentifier.utils';

export interface SlideRootProps {
  slideId: EducationListItemType;
  slideIndex: number;
  children?: React.ReactNode;
}

export function SlideRoot({ slideId, slideIndex, children }: SlideRootProps) {
  const {
    connectors: { connect },
  } = useNode();
  const isEditMode = useIsBlockEditorEditMode();

  // Important: We need to set the DOM ref so that Craft.js knows where this node is in the DOM.
  // This enables drag-and-drop interactions for its children (linked nodes).

  // For custom slides (CUSTOM_PAGE_*), use the Draft component
  // For template slides, use the slides getter
  const isCustomSlide = isCustomSlideIdentifier(slideId);
  const override = useEducationSlideOverride(
    isCustomSlide
      ? null
      : getSlideIdentifierFromEducationSlideId(slideId, slideIndex)
  );
  const slides = getEducationSlides(slideId);
  const SlideComponent = isCustomSlide ? Draft : slides?.[slideIndex];

  const attachRef = useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref && isEditMode) {
        // Only connect, don't make draggable
        connect(ref);
      }
    },
    [connect, isEditMode]
  );

  if (!SlideComponent) {
    return null;
  }

  return (
    <Box
      ref={attachRef}
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Propagate flex growth to the page Stack so the
        // TwoColumnLayout divider can span the full slide height
        '& > .MuiStack-root': { flex: 1 },
      }}
    >
      <SlideComponent
        hideWealthDistributionGraph={
          override?.contentJSON.educationSlideOptions
            ?.hideWealthDistributionGraph === true
        }
      />
      {/* Render children (dropped items) */}
      {children}
    </Box>
  );
}

SlideRoot.craft = {
  displayName: 'Slide Root',
  props: {
    slideId: null,
    slideIndex: 0,
  },
  rules: {
    canDrag: () => false,
    canMoveIn: () => false, // Reject all drops (causes items to snap back)
  },
};
